/**
 * A seção José da administração (docs/spec/desk-tutor.md): as perguntas das últimas 24 horas, com o
 * gasto estimado, o modelo da IA e os limites do José. Todo admin vê; só os da configuração do
 * servidor mudam, como nos planos.
 */
import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useLanguage, useT } from '../i18n/index.js';
import { DeskTutorSettingsSchema, deskTutorModelLabel, deskTutorProviderOf, type AdminDeskTutorInfo, type DeskTutorSettings } from '../shared.js';
import { describeError, useApp } from '../store.js';
import { Button, ComboBox, Spinner, Switch, TextEdit } from '../ui/controls.js';
import { toast } from '../ui/Toaster.js';
import { useDateTimeText } from './AdminLog.js';

/** Os três números, como o campo de texto os guarda, e o modelo ('' quando o José não existe). */
type Draft = { enabled: boolean; perUserDaily: string; minIntervalSeconds: string; serverDaily: string; model: string };

const draftOf = (settings: DeskTutorSettings, model: string | null): Draft => ({
  enabled: settings.enabled,
  perUserDaily: String(settings.perUserDaily),
  minIntervalSeconds: String(settings.minIntervalSeconds),
  serverDaily: String(settings.serverDaily),
  model: model ?? '',
});

/** O rascunho como limites, ou null quando algum número não vale (vazio, quebrado ou fora da faixa). */
function settingsOf(draft: Draft): DeskTutorSettings | null {
  const number = (text: string) => (/^\d+$/.test(text.trim()) ? Number(text.trim()) : NaN);
  const parsed = DeskTutorSettingsSchema.safeParse({
    enabled: draft.enabled,
    perUserDaily: number(draft.perUserDaily),
    minIntervalSeconds: number(draft.minIntervalSeconds),
    serverDaily: number(draft.serverDaily),
  });
  return parsed.success ? parsed.data : null;
}

export function AdminDeskTutor({ version, onChanged }: { version: number; onChanged: () => void }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const token = useApp((s) => s.token);
  const when = useDateTimeText();
  const [info, setInfo] = useState<AdminDeskTutorInfo | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.adminDeskTutor(token).then(
      (result) => {
        if (!alive) return;
        setInfo(result);
        setDraft(draftOf(result.settings, result.model));
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, version]);

  if (error) {
    return (
      <p className="rd-adminUsers__error" role="alert">
        {error}
      </p>
    );
  }
  if (!info || !draft) {
    return (
      <div className="rd-placeholder">
        <Spinner size={28} />
      </div>
    );
  }

  const settings = settingsOf(draft);
  const changed = settings !== null && (JSON.stringify(settings) !== JSON.stringify(info.settings) || draft.model !== (info.model ?? ''));
  const save = async () => {
    if (!token || !settings) return;
    setSaving(true);
    try {
      const result = await api.saveAdminDeskTutor(token, { ...settings, ...(draft.model ? { model: draft.model } : {}) });
      setInfo(result);
      setDraft(draftOf(result.settings, result.model));
      toast(t('admin.tutor.saved'), 'success');
      onChanged();
    } catch (failure) {
      toast(describeError(failure), 'error');
    } finally {
      setSaving(false);
    }
  };
  const money = (usd: number | null) =>
    usd === null ? '—' : new Intl.NumberFormat(language, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: usd < 1 ? 4 : 2 }).format(usd);
  const field = (key: 'perUserDaily' | 'minIntervalSeconds' | 'serverDaily') => (
    <div className="rd-adminTutor__field">
      <label htmlFor={`tutor-${key}`}>{t(`admin.tutor.${key}`)}</label>
      <TextEdit
        id={`tutor-${key}`}
        look="filled"
        inputMode="numeric"
        value={draft[key]}
        onChange={(value) => setDraft({ ...draft, [key]: value })}
        disabled={!info.canEdit}
        invalid={settingsOf({ ...draftOf(info.settings, info.model), [key]: draft[key] }) === null}
      />
    </div>
  );

  return (
    <section className="rd-adminServer rd-adminTutor" aria-label={t('admin.section.tutor')}>
      <p className="rd-adminPlans__hint">{info.canEdit ? t('admin.tutor.hint') : t('admin.tutor.readOnly')}</p>
      {info.model === null ? (
        <p className="rd-adminTutor__off" role="status">
          {t('admin.tutor.noKey')}
        </p>
      ) : null}

      <h3 className="rd-adminOverview__group">{t('admin.tutor.day')}</h3>
      <dl className="rd-adminServer__facts">
        <dt>{t('admin.tutor.answered')}</dt>
        <dd>
          <span>{info.day.answered}</span>
        </dd>
        <dt>{t('admin.tutor.people')}</dt>
        <dd>
          <span>{info.day.people}</span>
        </dd>
        <dt>{t('admin.tutor.failed')}</dt>
        <dd>
          <span>{info.day.failed}</span>
        </dd>
        <dt>{t('admin.tutor.cost')}</dt>
        <dd>
          <span>{money(info.day.costUsd)}</span>
        </dd>
        <dt>{t('admin.tutor.model')}</dt>
        <dd>
          <span>{info.model ? deskTutorModelLabel(info.model) : t('admin.server.none')}</span>
        </dd>
        <dt>{t('admin.tutor.defaultModel')}</dt>
        <dd>
          <span>{info.defaultModel ? deskTutorModelLabel(info.defaultModel) : t('admin.server.none')}</span>
          <code>RPGDESK_ASSISTANT_MODEL</code>
        </dd>
      </dl>

      <h3 className="rd-adminOverview__group">{t('admin.tutor.limits')}</h3>
      <div className="rd-adminTutor__form">
        <div className="rd-adminTutor__switch">
          <Switch id="tutor-enabled" aria-describedby="tutor-enabled-desc" checked={draft.enabled} disabled={!info.canEdit} onChange={(enabled) => setDraft({ ...draft, enabled })} />
          <label htmlFor="tutor-enabled">{t('admin.tutor.enabled')}</label>
          <p id="tutor-enabled-desc">{t('admin.tutor.enabledHint')}</p>
        </div>
        {info.model !== null ? (
          <div className="rd-adminTutor__field rd-adminTutor__field--wide">
            <label htmlFor="tutor-model">{t('admin.tutor.modelField')}</label>
            {info.canEdit ? (
              <ComboBox
                id="tutor-model"
                value={draft.model}
                onChange={(model) => setDraft({ ...draft, model })}
                options={info.models
                  .filter((m) => m.available)
                  .map((m) => ({ value: m.id, label: m.score !== null ? t('admin.tutor.modelScore', { label: m.label, score: m.score }) : m.label }))}
              />
            ) : (
              <span>{deskTutorModelLabel(info.model)}</span>
            )}
          </div>
        ) : null}
        {draft.model && deskTutorProviderOf(draft.model) === 'deepseek' ? (
          <p className="rd-adminTutor__warning" role="note">
            {t('admin.tutor.deepseekPrivacy')}
          </p>
        ) : null}
        {info.model !== null && !info.models.some((m) => m.provider === 'deepseek' && m.available) ? <p className="rd-adminTutor__meta">{t('admin.tutor.deepseekKey')}</p> : null}
        {field('perUserDaily')}
        {field('minIntervalSeconds')}
        {field('serverDaily')}
        {settings === null ? (
          <p className="rd-adminTutor__warning" role="alert">
            {t('admin.tutor.range')}
          </p>
        ) : null}
        {info.canEdit ? (
          <div className="rd-adminTutor__actions">
            <Button variant="action" disabled={!changed} busy={saving} onClick={() => void save()}>
              {t('admin.tutor.save')}
            </Button>
          </div>
        ) : null}
        {info.updatedAt !== null ? <p className="rd-adminTutor__meta">{t('admin.tutor.updated', { name: info.updatedByName ?? '—', when: when(info.updatedAt) })}</p> : null}
      </div>
    </section>
  );
}
